import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function FindingOverview() {
  const navigate = useNavigate();

  const totalFindings = 126;

  // Findings by Severity
  const findingsBySeverity = {
    labels: ['High', 'Medium', 'Low'],
    datasets: [{
      data: [18, 45, 63],
      backgroundColor: ['#C5504F', '#FFC000', '#5B9BD5'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Findings Over Time (last 5 months)
  const findingsOverTime = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
    datasets: [{
      label: 'Findings',
      data: [32, 28, 35, 38, 42],
      borderColor: '#4472C4',
      backgroundColor: 'rgba(68, 114, 196, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  };

  // Findings by Model Category
  const findingsByCategory = {
    labels: ['Credit Risk', 'Market Risk', 'Operational Risk', 'Compliance Risk', 'Liquidity Risk', 'Other'],
    datasets: [{
      label: 'Number of Findings',
      data: [35, 28, 22, 18, 12, 11],
      backgroundColor: '#4472C4',
      borderRadius: 4
    }]
  };

  // Mock findings details table
  const findingsDetails = [
    { id: 'F-001', model: 'Credit Risk Model 1', category: 'Credit Risk', severity: 'High', status: 'Open', owner: 'John Doe', date: '15/05/2026' },
    { id: 'F-002', model: 'Fraud Detection V2', category: 'Fraud', severity: 'Medium', status: 'In Progress', owner: 'Jane Smith', date: '12/05/2026' },
    { id: 'F-003', model: 'Market Risk Calculator', category: 'Market Risk', severity: 'High', status: 'Open', owner: 'Bob Wilson', date: '10/05/2026' },
    { id: 'F-004', model: 'Operational Risk Assessment', category: 'Operational', severity: 'Low', status: 'Resolved', owner: 'Alice Brown', date: '08/05/2026' },
    { id: 'F-005', model: 'Credit Scoring Model', category: 'Credit Risk', severity: 'Medium', status: 'In Progress', owner: 'Charlie Davis', date: '05/05/2026' },
    { id: 'F-006', model: 'Trading Strategy Model 1', category: 'Market Risk', severity: 'Low', status: 'Open', owner: 'David Lee', date: '03/05/2026' },
    { id: 'F-007', model: 'Liquidity Risk Model', category: 'Liquidity', severity: 'Medium', status: 'Open', owner: 'Eva Garcia', date: '01/05/2026' },
    { id: 'F-008', model: 'Compliance Model V3', category: 'Compliance', severity: 'High', status: 'In Progress', owner: 'Frank White', date: '28/04/2026' }
  ];

  const donutChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10
        }
      }
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-exclamation-diamond me-2"></i>
                Finding Overview
              </h2>
              <p className="text-muted mb-0">Comprehensive Findings Analysis and Tracking</p>
            </div>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
          </div>
        </div>
      </div>

      {/* Top Summary Cards */}
      <div className="row g-4 mb-4">
        {/* Total Findings */}
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h5 className="card-title text-muted mb-3">Total Findings</h5>
              <h1 className="display-2 fw-bold text-primary">{totalFindings}</h1>
              <p className="text-muted mb-0 mt-3">
                <i className="bi bi-arrow-up text-danger me-1"></i>
                +12% from last month
              </p>
            </div>
          </div>
        </div>

        {/* Findings by Severity */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Findings by Severity</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '200px' }}>
                <Doughnut data={findingsBySeverity} options={donutChartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-4">Status Breakdown</h5>
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <span className="text-muted">Open</span>
                <div>
                  <span className="badge bg-danger me-2">45</span>
                  <span className="text-muted small">36%</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <span className="text-muted">In Progress</span>
                <div>
                  <span className="badge bg-warning text-dark me-2">52</span>
                  <span className="text-muted small">41%</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Resolved</span>
                <div>
                  <span className="badge bg-success me-2">29</span>
                  <span className="text-muted small">23%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-4 mb-4">
        {/* Findings Over Time */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Findings Over Time</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '250px' }}>
                <Line data={findingsOverTime} options={lineChartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Findings by Model Category */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Findings by Model Category</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '250px' }}>
                <Bar data={findingsByCategory} options={barChartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Findings Details Table */}
      <div className="row g-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-table me-2"></i>
                Recent Findings
              </h5>
              <div className="btn-group btn-group-sm" role="group">
                <button type="button" className="btn btn-outline-secondary active">All</button>
                <button type="button" className="btn btn-outline-secondary">High</button>
                <button type="button" className="btn btn-outline-secondary">Medium</button>
                <button type="button" className="btn btn-outline-secondary">Low</button>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Finding ID</th>
                      <th>Model Name</th>
                      <th>Category</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Owner</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findingsDetails.map((finding, index) => (
                      <tr key={index}>
                        <td className="fw-bold">{finding.id}</td>
                        <td>{finding.model}</td>
                        <td>
                          <span className="badge bg-light text-dark">{finding.category}</span>
                        </td>
                        <td>
                          <span className={`badge ${
                            finding.severity === 'High' ? 'bg-danger' :
                            finding.severity === 'Medium' ? 'bg-warning text-dark' :
                            'bg-success'
                          }`}>
                            {finding.severity}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${
                            finding.status === 'Open' ? 'bg-danger bg-opacity-25 text-danger' :
                            finding.status === 'In Progress' ? 'bg-warning bg-opacity-25 text-warning' :
                            'bg-success bg-opacity-25 text-success'
                          }`}>
                            {finding.status}
                          </span>
                        </td>
                        <td>{finding.owner}</td>
                        <td>{finding.date}</td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-1">
                            <i className="bi bi-eye"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-secondary">
                            <i className="bi bi-pencil"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-white border-top">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Showing 8 of {totalFindings} findings</span>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className="page-item disabled">
                      <span className="page-link">Previous</span>
                    </li>
                    <li className="page-item active"><span className="page-link">1</span></li>
                    <li className="page-item"><button className="page-link" onClick={() => {}}>2</button></li>
                    <li className="page-item"><button className="page-link" onClick={() => {}}>3</button></li>
                    <li className="page-item">
                      <button className="page-link" onClick={() => {}}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FindingOverview;
